import { useBooking } from '@/Stores/booking.js'
import { useGetInTouch } from '@/Stores/getintouch.js'
import { formatDateNumeric } from '@/Composables/common.js'
import { usePage } from '@inertiajs/vue3'
import { useGtmEvent } from '@/Composables/gtm.js'

export async function useFetchSearch(payload) {
	const booking = useBooking()
	const page = usePage()

	booking.isSubmitting = true

	payload
		.transform((payload) => ({
			...payload,
			pickupdate: formatDateNumeric(payload.pickupdate),
			dropoffdate: formatDateNumeric(payload.dropoffdate),
		}))
		.get(route('booking.search'), {
			preserveScroll: true,
			onSuccess: async () => {
				booking.setAvailableResults(page.props.data)
				booking.isSubmitting = false

				// Set payload for event data.
				const bookingdata = {
					pickuplocation: booking.getSelectedPickup.location,
					pickupdate: formatDateNumeric(
						booking.getSelectedPickup.date
					),
					pickuptime: booking.getSelectedPickup.time,
					dropofflocation: booking.getSelectedDropoff.location,
					dropoffdate: formatDateNumeric(
						booking.getSelectedDropoff.date
					),
					dropofftime: booking.getSelectedDropoff.time,
					driverage: booking.getSelectedAge.value,
					promocode: booking.selected.campaigncode || null,
				}

				// Push event and payload to GTM.
				useGtmEvent('step1_booking', bookingdata)

				if (route().current('select')) {
					scrollTo('#selection')
				}

				booking.isFromHome = false
			},
			onError: (errors) => {
				booking.errors = errors
				booking.isSubmitting = false
			},
		})
}

export async function useFetchExtras(payload, isPreserveScroll = false) {
	const booking = useBooking()
	booking.isLoading = true

	payload.get(route('booking.show'), {
		preserveScroll: isPreserveScroll,
		queryStringArrayFormat: 'indices',
		onSuccess: () => {
			booking.isLoading = false

			// Set payload for event data.
			const vehicledata = {
				cartype: booking.getSelectedVehicle.type,
				model: booking.getSelectedVehicle.model,
				price: booking.getSelectedVehicle.totalrate,
				dailyrate: booking.getSelectedVehicle.dailyrate,
			}

			// Push event and payload to GTM.
			useGtmEvent('step2_vehicle', vehicledata)
		},
		onError: (errors) => {
			booking.errors = errors
			booking.isLoading = false
		},
	})
}

export async function useFetchPayment(payload, isPreserveScroll = false) {
	const booking = useBooking()
	booking.isLoading = true

	const page = usePage()
	const countryOptions = page.props.data?.options?.countries || []

	payload
		.transform((payload) => ({
			...payload,
			customer: {
				...payload.customer,
				dateofbirth: formatDateNumeric(payload.customer.dateofbirth),
				licenseexpires: formatDateNumeric(
					payload.customer.licenseexpires
				),
			},
		}))
		.post(route('payment.create'), {
			preserveScroll: isPreserveScroll,
			queryStringArrayFormat: 'indices',
			onSuccess: () => {
				booking.isLoading = false

				booking.selected.customer = {
					...payload.customer,
					countryname:
						countryOptions.find(
							(country) =>
								country.id === payload.customer?.countryid
						)?.country || '',
				}

				// Set payload for event data.
				const extradata = {
					babyseat: booking.isBabySeatSelected,
					boosterseat: booking.isBoosterSeatSelected,
					insurancestandard: booking.isInsuranceStandardSelected,
					insurancelooseexcess:
						booking.isInsuranceLooseExcessSelected,
					extrafees: booking.getSelectedExtraOptionalTotal,
				}

				// Push event and payload to GTM.
				useGtmEvent('step3_extras', extradata)
			},
			onError: (errors) => {
				booking.errors = errors
				booking.isLoading = false
			},
		})
}

export async function useFetchBooking(payload, isPreserveScroll = false) {
	const booking = useBooking()
	booking.isLoading = true
	booking.isSubmitting = true

	payload.post(route('booking.store'), {
		preserveScroll: isPreserveScroll,
		queryStringArrayFormat: 'indices',
		onSuccess: () => {
			booking.isLoading = false
			booking.isSubmitting = false

			// Set payload for event data.
			const customerdata = {
				firstname: booking.getCustomerDetails.firstname,
				lastname: booking.getCustomerDetails.lastname,
				email: booking.getCustomerDetails.email,
				mobile: booking.getCustomerDetails.mobile,
				dateofbirth: formatDateNumeric(
					booking.getCustomerDetails.dateofbirth
				),
				country: booking.getCustomerDetails.countryname,
			}

			const paymentdata = {
				paymentmethod: booking.getSelectedPayment.method,
				transactionid: booking.getSelectedPayment.transactionid,
			}

			// Push event and payload to GTM.
			useGtmEvent('rentaldatacaptured', customerdata, paymentdata)
		},
		onError: (errors) => {
			booking.errors = errors
			booking.isLoading = false
			booking.isSubmitting = false
		},
	})
}

export async function useFetchGetInTouch(payload) {
	const getInTouch = useGetInTouch()
	getInTouch.isLoading = true
	payload.post(route('enquiry.get-in-touch'), {
		preserveState: true,
		preserveScroll: true,
		onSuccess: () => {
			getInTouch.isLoading = false
			getInTouch.isSubmitted = true
		},
		onError: (errors) => {
			getInTouch.errors = errors
			getInTouch.isLoading = false
			getInTouch.isSubmitted = false
		},
	})
}

export async function useFetchIdle(payload) {
	const getInTouch = useGetInTouch()
	getInTouch.isLoading = true
	payload.post(route('enquiry.support'), {
		preserveState: true,
		preserveScroll: true,
		onSuccess: () => {
			getInTouch.isLoading = false
			getInTouch.isSubmitted = true
		},
		onError: (errors) => {
			getInTouch.errors = errors
			getInTouch.isLoading = false
			getInTouch.isSubmitted = false
		},
	})
}
